import React, { useCallback } from 'react';

import { useQuery } from '@apollo/client';
import { Container, Typography } from '@mui/material';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import { InstantSearch } from 'react-instantsearch-hooks-web';
import { Link, useHistory } from 'react-router-dom';

import IKImage from '../../components/IKImage/IKImage';
import SearchBox from '../../components/SearchBox/SearchBox';
import { DEFAULT_DISCOVER_URL } from '../../const';
import { GET_HAS_NEWS } from '../../graphql/local';
import styled, { css } from '../../styled';
import {
  colorsPalette,
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
} from '../../theme';
import { Background } from '../../types';
import useSearchClient from '../../utils/hooks/useSearchClient';

const SearchBlock = ({ data }) => {
  const { indexName: algoliaIndexName, loadingApiKey, searchClient } = useSearchClient();
  const { t } = useTranslation();
  const history = useHistory();

  const isDark = data?.background === Background.Dark;
  const { data: newsData } = useQuery(GET_HAS_NEWS);
  const hasNews = newsData?.hasNews;

  const onChange = useCallback(
    ({ uiState }) => {
      const queryString = qs.stringify(
        { s: uiState?.[algoliaIndexName].query },
        {
          addQueryPrefix: true,
          arrayFormat: 'repeat',
        },
      );

      if (queryString) {
        history.push(`${DEFAULT_DISCOVER_URL}/search${queryString}`);
      }
    },
    [algoliaIndexName, history],
  );

  return (
    <Wrapper isDark={isDark}>
      {data.background_image_url && (
        <BackgroundImage path={data.background_image_url} alt={data.background_image_seo_name} />
      )}
      <StyledContainer>
        {data?.search_block_title && <Title variant="h2">{data?.search_block_title}</Title>}
        {data?.subtitle && <Subtitle>{data?.subtitle}</Subtitle>}
        <InstantSearch
          onStateChange={onChange}
          searchClient={searchClient}
          indexName={algoliaIndexName}
        >
          {!loadingApiKey && (
            <SearchBox
              withClear
              placeholder={data.input_placeholder}
              wrapperStyles={searchWrapper}
            />
          )}
        </InstantSearch>
        <Links>
          <StyledLink to={`${DEFAULT_DISCOVER_URL}/search`}>{t('page.discover.all')}</StyledLink>
          <StyledLink to={`${DEFAULT_DISCOVER_URL}/videos`}>
            {t('page.discover.videoModules')}
          </StyledLink>
          <StyledLink to={`${DEFAULT_DISCOVER_URL}/pathways`}>
            {t('page.discover.pathways')}
          </StyledLink>
          {hasNews && (
            <StyledLink to={`${DEFAULT_DISCOVER_URL}/insights`}>
              {t('page.discover.text')}
            </StyledLink>
          )}
          <StyledLink to={`${DEFAULT_DISCOVER_URL}/glossary`}>
            {t('page.discover.glossary')}
          </StyledLink>
        </Links>
      </StyledContainer>
    </Wrapper>
  );
};

export default SearchBlock;

const BackgroundImage = styled(IKImage)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.2;
`;
const searchWrapper = css`
  && {
    margin-bottom: ${MIDDLE_MARGIN_PX};
  }
`;
const Title = styled(Typography)`
  text-align: center;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-bottom: ${SMALL_MARGIN_PX};
  }

  &:first-letter {
    text-transform: uppercase;
  }
`;
const Subtitle = styled(Typography)`
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
  text-align: center;
  font-size: 16px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${MIDDLE_MARGIN_PX};
    font-size: 18px;
  }

  &:first-letter {
    text-transform: uppercase;
  }
`;
const StyledLink = styled(Link)`
  color: ${colorsPalette.white};
  margin: 0 ${TINY_MARGIN_PX} ${TINY_MARGIN_PX};
  text-align: center;
  text-transform: uppercase;
  font-weight: 700;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin: 0 ${SMALL_MARGIN_PX};
  }

  &:first-of-type {
    margin-left: 0;
  }

  &:last-of-type {
    margin-right: 0;
  }

  &:hover {
    color: ${colorsPalette.blue};
  }
`;
const StyledContainer = styled(Container)`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isDark',
})`
  position: relative;
  z-index: 2;
  background: ${({ theme }) => theme.palette.secondary.dark};
  padding: 80px 0 110px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    background: ${({ isDark, theme }) =>
      isDark ? theme.palette.secondary.dark : theme.palette.secondary.light};
    padding: 80px 0 120px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: ${LARGE_MARGIN_PX} 0 ${LARGE_MARGIN_PX};
  }
`;
const Links = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin: 0 auto;

  @media (min-width: 400px) {
    flex-wrap: nowrap;
  }
`;
